import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  Input,
  Field,
  Alert,
  Card,
  Spinner,
  SegmentGroup,
} from '@chakra-ui/react';
import { useAuth } from '../AuthContext';
import { useEnvironment } from '../contexts/EnvironmentContext';

function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login, error, loading } = useAuth();
  const { environment, isDevelopment } = useEnvironment();

  const handleEnvironmentChange = (details) => {
    const newEnvironment = details?.value;
    if (newEnvironment) {
      localStorage.setItem('selectedEnvironment', newEnvironment);
      window.location.reload();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const success = await login(username, password);
      if (success) {
        navigate('/');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      py="12"
      px="4"
      bg="#0a0a0a"
    >
      <Card.Root
        variant="outline"
        p="8"
        width="full"
        maxW="400px"
        borderRadius="8px"
        bg="#1a1a1a"
        border="1px solid rgba(0, 229, 255, 0.2)"
      >
        <Card.Body>
          <VStack gap="4" align="stretch">
            <Heading as="h1" size="xl" textAlign="center" color="white">
              Paradise Admin
            </Heading>
            <Text textAlign="center" color="fg.muted">
              Login to access the admin panel
            </Text>

            <Box display="flex" justifyContent="center" mt="2">
              <SegmentGroup.Root
                value={environment}
                onValueChange={(details) => handleEnvironmentChange(details)}
                size="sm"
              >
                <SegmentGroup.Indicator />
                <SegmentGroup.Item value="production">
                  <SegmentGroup.ItemText>PRODUCTION</SegmentGroup.ItemText>
                  <SegmentGroup.ItemHiddenInput />
                </SegmentGroup.Item>
                <SegmentGroup.Item value="development">
                  <SegmentGroup.ItemText>DEVELOPMENT</SegmentGroup.ItemText>
                  <SegmentGroup.ItemHiddenInput />
                </SegmentGroup.Item>
              </SegmentGroup.Root>
            </Box>

            {error && (
              <Alert.Root status="error" colorPalette="red">
                <Alert.Indicator />
                <Alert.Title>{error}</Alert.Title>
              </Alert.Root>
            )}

            <form onSubmit={handleSubmit}>
              <VStack gap="4" align="stretch">
                <Field.Root>
                  <Field.Label>Username</Field.Label>
                  <Input
                    required
                    id="username"
                    name="username"
                    autoComplete="username"
                    autoFocus
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={submitting || loading}
                    bg="#2a2a2a"
                    borderColor="rgba(0, 229, 255, 0.3)"
                    _hover={{ borderColor: 'rgba(0, 229, 255, 0.6)' }}
                    _focus={{ borderColor: '#00e5ff', borderWidth: '2px' }}
                    color="white"
                  />
                </Field.Root>
                <Field.Root>
                  <Field.Label>Password</Field.Label>
                  <Input
                    required
                    name="password"
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={submitting || loading}
                    bg="#2a2a2a"
                    borderColor="rgba(0, 229, 255, 0.3)"
                    _hover={{ borderColor: 'rgba(0, 229, 255, 0.6)' }}
                    _focus={{ borderColor: '#00e5ff', borderWidth: '2px' }}
                    color="white"
                  />
                </Field.Root>
                <Button
                  type="submit"
                  width="full"
                  colorPalette="cyan"
                  disabled={submitting || loading || !username || !password}
                  mt="2"
                >
                  {submitting || loading ? (
                    <Spinner size="sm" />
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </VStack>
            </form>
          </VStack>
        </Card.Body>
      </Card.Root>
    </Box>
  );
}

export default Login;
